// @flow
/* eslint-disable ta-code-conventions/has-matching-test, ta-code-conventions/has-matching-storydoc */

import * as React from 'react';
import { RouteConsumer } from '../RouterContext';
import { type PreloadIds } from '../types';
import { buildTripModal } from './modals';

/** Function to build the path to Profile page */
export const build = buildTripModal(
  (
    servlet: string,
    { modal, card, username, feedIdToRestore, fid, ...params }: { [string]: string | number },
  ) => {
    let path = servlet;

    if (username) {
      path += `/${username}`;
    }

    // See ProfileServletParameterHandler.java for any additions/subtractions
    const modalCards = [
      'LinkPost',
      'ForumPost',
      'Repost',
      'Review',
      'Photo',
      'Video',
      'MediaBatch',
    ];

    const adjustedParams: any = params;
    let fidAdjusted = fid;

    // Only reconstruct the url on whitelist of modal/card types
    if (modal && typeof modal === 'string') {
      const parsedModal = modal.split('_');
      if (modalCards.includes(parsedModal[0])) {
        path += `/${parsedModal.join('/')}`;
      } else {
        path += `/${modal}`;
      }
      // fid does not apply to modals
      fidAdjusted = undefined;
    } else if (!('tab' in params) && card && typeof card === 'string') {
      const parsedCard = card.split('_');
      if (modalCards.includes(parsedCard[0])) {
        path += `/${parsedCard.join('/')}`;
      } else {
        path += `/${card}`;
      }
    }

    // re-add fid if still applicable
    if (fidAdjusted) {
      adjustedParams.fid = fid;
    }

    return { path, ...adjustedParams };
  },
);

const parseModalOrCardValue = (value: string): PreloadIds => {
  const preloadIds: PreloadIds = {
    preloadForumPostIds: [],
    preloadLinkPostIds: [],
    preloadPhotoIds: [],
    preloadMediaBatchIds: [],
    preloadRepostIds: [],
    preloadReviewIds: [],
    preloadVideoIds: [],
  };

  if (!value) {
    return preloadIds;
  }

  const parsed = value.split('_');
  const parsedId = Number(parsed[1]);
  const isValidParsedId = !isNaN(parsedId);

  if (isValidParsedId) {
    switch (parsed[0]) {
      case 'ForumPost':
        preloadIds.preloadForumPostIds = [parsedId];
        break;
      case 'LinkPost':
        preloadIds.preloadLinkPostIds = [parsedId];
        break;
      case 'Photo':
        preloadIds.preloadPhotoIds = [parsedId];
        break;
      case 'Repost':
        preloadIds.preloadRepostIds = [parsedId];
        break;
      case 'Review':
        preloadIds.preloadReviewIds = [parsedId];
        break;
      case 'Video':
        preloadIds.preloadVideoIds = [parsedId];
        break;
      case 'MediaBatch':
        preloadIds.preloadMediaBatchIds = [parsedId];
        break;
      default:
    }
  }

  return preloadIds;
};

/** Function to get preload ids */
export const getPreloadIds = (route: { [string]: any }): PreloadIds => {
  const { modal, card } = route;

  if (modal) {
    return parseModalOrCardValue(modal);
  } else if (card) {
    return parseModalOrCardValue(card);
  }

  return {
    preloadForumPostIds: [],
    preloadLinkPostIds: [],
    preloadPhotoIds: [],
    preloadMediaBatchIds: [],
    preloadRepostIds: [],
    preloadReviewIds: [],
    preloadVideoIds: [],
  };
};

/** Function to parse username */
export const parse = (username: string) => ({ username });

type Props = {|
  children: any => React.Node,
|};

const ProfileRoute = ({ children }: Props) => (
  // $FlowFixMe no param 'edit' in RouteConsumer
  <RouteConsumer>
    {({ edit, ...route }) =>
      children({
        ...route,
        edit,
        editOn: { ...route, edit: '' },
        editOff: { ...route },
      })
    }
  </RouteConsumer>
);

export default ProfileRoute;
